// frontend/modules/template/components/TransformationResultPreview.tsx
import React from 'react';
import {
    Card,
    Typography,
    Descriptions,
    Tag,
    Divider,
    Alert,
    Spin,
    Tabs,
    Row,
    Col,
    Space,
    Badge
} from 'antd';
import {
    ClockCircleOutlined,
    CheckCircleOutlined,
    CloseCircleOutlined,
    InfoCircleOutlined,
    UserOutlined,
    MailOutlined,
    PhoneOutlined,
    TeamOutlined
} from '@ant-design/icons';
import { NotificationTestResult, ContextTransformationResult } from '@/shared/api/model';

const { Title, Text, Paragraph } = Typography;

// Type guard to check if result is NotificationTestResult
const isNotificationTestResult = (result: any): result is NotificationTestResult => {
    return result && typeof result === 'object' && 'overallSuccess' in result;
};

interface TransformationResultPreviewProps {
    result: NotificationTestResult | ContextTransformationResult | null;
    loading?: boolean;
}

export const TransformationResultPreview: React.FC<TransformationResultPreviewProps> = ({
    result,
    loading = false
}) => {
    console.log('result', result)
    if (loading) {
        return (
            <Card className="mt-4 min-h-[400px]">
                <div className="py-16 flex justify-center">
                    <Spin size="large" tip="Transforming context..." />
                </div>
            </Card>
        );
    }

    if (!result) {
        return null;
    }
    console.log('result', result);

    // Check for overall failure first
    if (isNotificationTestResult(result) && !result.overallSuccess) {
        // Get the error message from the appropriate location
        const errorMessage = result.transformation?.errorMessage || 
                            result.message || 
                            "An unknown error occurred during transformation";
        
        return (
            <Card className="mt-4 min-h-[400px]">
                <Alert
                    message="Transformation Failed"
                    description={errorMessage}
                    type="error"
                    showIcon
                />
                {result.transformation?.scriptIdentifier && (
                    <div className="mt-4">
                        <Text type="secondary">Script: {result.transformation.scriptIdentifier}</Text>
                    </div>
                )}
            </Card>
        );
    }
    
    // Use the transformation data if it exists, otherwise fall back to result
    const transformationData = isNotificationTestResult(result) ? result.transformation : result;
    
    // Check if the transformation was successful
    if (!transformationData || !transformationData.success) {
        return (
            <Card className="mt-4 min-h-[400px]">
                <Alert
                    message="Transformation Failed"
                    description={transformationData?.errorMessage || "An unknown error occurred during transformation"}
                    type="error"
                    showIcon
                />
                {transformationData?.scriptIdentifier && (
                    <div className="mt-4">
                        <Text type="secondary">Script: {transformationData?.scriptIdentifier}</Text>
                    </div>
                )}
            </Card>
        );
    }

    // Extract contact groups and contacts from the new format
    const transformedContext = transformationData.transformedContext || {};
    const availableContactGroups = transformedContext.availableContactGroups || {};
    const contacts = transformedContext.contacts || [];
    const subjectPrefix = transformedContext.subjectPrefix;
    const priority = transformedContext.priority;
    const approvalUrl = transformedContext.approvalUrl;

    // Get applicant details from transformed context
    const applicantDetails = transformedContext.applicantDetails || {};
    const workflow = transformedContext.workflow || {};
    const metadata = transformedContext.metadata || {};

    const tabItems = [
        {
            key: "overview",
            label: "Overview",
            children: (
                <div className="space-y-6">
                    {/* Transformation Status */}
                    <div className="flex justify-between items-center">
                        <Space>
                            <Tag icon={<CheckCircleOutlined />} color="success">
                                Transformation Successful
                            </Tag>
                            {transformationData.executionTimeMs !== undefined && (
                                <Tag icon={<ClockCircleOutlined />} color="blue">
                                    {transformationData.executionTimeMs}ms
                                </Tag>
                            )}
                            {priority && (
                                <Tag color={priority.toString() === 'High' ? 'red' : 'default'}>
                                    {priority.toString()} Priority
                                </Tag>
                            )}
                        </Space>
                    </div>


                    
                    {/* Recipients Status */}
                    {isNotificationTestResult(result) && result.resolvedRecipients && result.resolvedRecipients?.totalRecipients && (
                        <Card size="small" title="Recipients Status">
                            <Alert
                                type={result.resolvedRecipients?.totalRecipients > 0 ? "success" : "warning"}
                                message={result.resolvedRecipients?.totalRecipients > 0
                                    ? `Found ${result.resolvedRecipients?.totalRecipients} recipients`
                                    : "No recipients found"}
                                description={result.resolvedRecipients.summary || "Recipient resolution complete."}
                                showIcon
                            />
                        </Card>
                    )}
                </div>
            )
        },
        {
            key: "contacts",
            label: "Contact Groups",
            children: (
                <div className="space-y-4">
                    {/* Available Contact Groups */}
                    <Card size="small" title="Available Contact Groups">
                        <div className="space-y-3">
                            {Object.entries(availableContactGroups).map(([key, group]: [string, any]) => (
                                <div key={key} className="p-3 border rounded-lg">
                                    <div className="flex justify-between items-start">
                                        <div>
                                            <Text strong>{group.label}</Text>
                                            <div className="text-gray-600 text-sm mt-1">
                                                {group.description}
                                            </div>
                                        </div>
                                        <Tag color="blue">{key}</Tag>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </Card>

                    {/* Contact Details */}
                    <Card size="small" title="Contact Configuration">
                        <div className="space-y-3">
                            {// @ts-ignore
                                contacts.map((contact: any, index: number) => (
                                <div key={index} className="p-3 border rounded-lg">
                                    <div className="flex justify-between items-start mb-2">
                                        <div className="flex items-center space-x-2">
                                            {/*@ts-ignore*/}
                                            <Text strong>{availableContactGroups[contact.key]?.label || contact.key}</Text>
                                            {contact.primary && (
                                                <Tag color="gold">PRIMARY</Tag>
                                            )}
                                        </div>
                                        <div className="flex space-x-1">
                                            {contact.platforms?.map((platform: string) => (
                                                <Tag key={platform}  color="green">
                                                    {platform}
                                                </Tag>
                                            ))}
                                        </div>
                                    </div>
                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-2 text-sm">
                                        <div>
                                            <Text type="secondary">Type:</Text> {contact.type}
                                        </div>
                                        <div>
                                            <Text type="secondary">Value:</Text> {contact.value}
                                        </div>
                                        <div>
                                            <Text type="secondary">Needs Resolution:</Text>
                                            <Tag color={contact.needsResolution ? "orange" : "green"}>
                                                {contact.needsResolution ? "Yes" : "No"}
                                            </Tag>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </Card>
                </div>
            )
        },
        {
            key: "template",
            label: "Template Data",
            children: (
                <div className="space-y-4">
                    <Card size="small" title="Template Configuration">
                        <Descriptions column={2} size="small" bordered={false}>
                            <Descriptions.Item label="Subject Prefix">
                                {subjectPrefix?.toString() || '-'}
                            </Descriptions.Item>
                            <Descriptions.Item label="Priority">
                                <Tag color={priority?.toString() === 'High' ? 'red' : 'default'}>
                                    {priority?.toString() || 'Normal'}
                                </Tag>
                            </Descriptions.Item>
                            <Descriptions.Item label="Approval URL" span={2}>
                                {approvalUrl ? (
                                    <a href={approvalUrl?.toString()} target="_blank" rel="noopener noreferrer">
                                        {approvalUrl?.toString()}
                                    </a>
                                ) : '-'}
                            </Descriptions.Item>
                        </Descriptions>
                    </Card>

                    {/* Metadata */}
                    {Object.keys(metadata).length > 0 && (
                        <Card size="small" title="Additional Metadata">
                            <Descriptions column={2} size="small" bordered={false}>
                                {Object.entries(metadata).map(([key, value]: [string, any]) => (
                                    <Descriptions.Item key={key} label={key}>
                                        {Array.isArray(value) ? value.join(', ') : String(value)}
                                    </Descriptions.Item>
                                ))}
                            </Descriptions>
                        </Card>
                    )}
                </div>
            )
        },
        {
            key: "raw",
            label: "Raw Data",
            children: (
                <div className="space-y-4">
                    <Card size="small" title="Transformed Context">
                        <pre className="bg-gray-50 p-4 rounded-lg overflow-auto max-h-96 text-sm">
                            {JSON.stringify(transformedContext, null, 2)}
                        </pre>
                    </Card>

                    {transformationData.originalContextJson && (
                        <Card size="small" title="Original Context">
                            <pre className="bg-gray-50 p-4 rounded-lg overflow-auto max-h-96 text-sm">
                                {JSON.stringify(JSON.parse(transformationData.originalContextJson), null, 2)}
                            </pre>
                        </Card>
                    )}

                    {/* Execution Details */}
                    <Card size="small" title="Execution Details">
                        <Descriptions column={2} size="small" bordered={false}>
                            <Descriptions.Item label="Script Identifier">
                                {transformationData.scriptIdentifier || '-'}
                            </Descriptions.Item>
                            <Descriptions.Item label="Execution Time">
                                {transformationData.executionTimeMs ? `${transformationData.executionTimeMs}ms` : '-'}
                            </Descriptions.Item>
                            <Descriptions.Item label="Context Keys">
                                {Object.keys(transformedContext || {}).length}
                            </Descriptions.Item>
                            <Descriptions.Item label="Timestamp">
                                {transformationData.executionTimestamp ? new Date(transformationData.executionTimestamp).toLocaleString() : '-'}
                            </Descriptions.Item>
                        </Descriptions>
                    </Card>
                    
                    {/* Full Response */}
                    <Card size="small" title="Full Response">
                        <pre className="bg-gray-50 p-4 rounded-lg overflow-auto max-h-96 text-sm">
                            {JSON.stringify(result, null, 2)}
                        </pre>
                    </Card>
                </div>
            )
        }
    ];

    return (
        <Card className="mt-4" style={{ minHeight: '500px' }}>
            <div className="mb-4">
                <Title level={4}>
                    <InfoCircleOutlined className="mr-2" />
                    Transformation Result
                </Title>
            </div>

            <Tabs
                defaultActiveKey="overview"
                items={tabItems}
                className="h-full"
            />
        </Card>
    );
};